import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  TablePagination,
  Chip,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

function DriverManagement() {
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [newDriver, setNewDriver] = useState({
    fullName: "",
    email: "",
    password: "",
    routeIds: [],
  });
  const [editingDriver, setEditingDriver] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  useEffect(() => {
    fetchDrivers();
    fetchRoutes();
  }, []);

  console.log("selectedRoutes", selectedRoutes);
  const fetchDrivers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/drivers`);
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      } else {
        console.error("Failed to fetch drivers");
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/routes`);
      if (response.ok) {
        const data = await response.json();
        setRoutes(data);
      } else {
        console.error("Failed to fetch routes");
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
    }
  };

  useEffect(() => {
    setFilteredDrivers(
      drivers.filter((driver) =>
        driver.fullName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [drivers, searchQuery]);

  // Handle pagination
  const driverPagination = filteredDrivers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle add driver modal
  const handleAddDriver = () => {
    setOpenAddModal(true);
  };

  const handleSaveDriver = async () => {
    try {
      // Make POST request to create driver
      const response = await fetch(`${process.env.REACT_APP_API_URL}/driver`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: newDriver.fullName,
          email: newDriver.email,
          password: newDriver.password, // Assuming you want to pass password here
          routeIds: newDriver.routeIds,
        }),
      });

      // Check if response is successful
      if (response.ok) {
        const createdDriver = await response.json(); // Get the driver data from the response
        // Fetch updated drivers list
        fetchRoutes();
        fetchDrivers();

        // Update the local state to include the newly created driver
        setDrivers((prevDrivers) => [...prevDrivers, createdDriver]);

        // Reset the modal and input states
        setOpenAddModal(false);
        setNewDriver({ fullName: "", email: "", routeIds: [] });
        setSelectedRoutes([]);
      } else {
        console.error("Failed to add driver");
      }
    } catch (error) {
      console.error("Error adding driver:", error);
    }
  };

  // Edit Driver Handler
  const handleEditDriver = (driver) => {
    console.log("driver is", driver);
    setEditingDriver(driver); // Set the driver to be edited
    const routeIds = driver.deliveryRoutes?.map((route) => route._id) || [];
    setSelectedRoutes(routeIds);

    setOpenEditModal(true); // Open the Edit modal
  };

  // Save changes to the edited driver
  const handleSaveEditedDriver = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/driver/${editingDriver._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editingDriver),
        }
      );

      if (response.ok) {
        await fetchDrivers(); // Fetch the latest drivers
        await fetchRoutes(); // Fetch the latest drivers
        setOpenEditModal(false); // Close the Edit modal
        setEditingDriver(null); // Reset editing driver state
      } else {
        console.error("Failed to update driver");
      }
    } catch (error) {
      console.error("Error updating driver:", error);
    }
  };

  const handleDeleteOpen = (driverId) => {
    setSelectedDriverId(driverId);
    setOpenDeleteModel(true);
  };

  // Delete Driver Handler
  const handleDeleteDriver = async (driverId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/driver/${selectedDriverId}`,
        { method: "DELETE" }
      );

      if (response.ok) {
        setOpenDeleteModel(false);
        fetchDrivers();
        setDrivers((prevDrivers) =>
          prevDrivers.filter((driver) => driver._id !== driverId)
        );
      } else {
        console.error("Failed to delete driver");
      }
    } catch (error) {
      console.error("Error deleting driver:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleRouteSelect = (event) => {
    setSelectedRoutes(event.target.value);

    setNewDriver({ ...newDriver, routeIds: event.target.value });
  };
  const handleDeleteChip = (routeId) => {
    const updatedRoutes = selectedRoutes.filter((id) => id !== routeId);
    setSelectedRoutes(updatedRoutes);

    if (editingDriver) {
      setEditingDriver({ ...editingDriver, routeIds: updatedRoutes });
    } else {
      setNewDriver({ ...newDriver, routeIds: updatedRoutes });
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4">Driver Management</Typography>
      <Box display="flex" justifyContent="space-between" marginBottom={2}>
        <TextField
          placeholder="Search Drivers"
          label="Search Drivers"
          variant="outlined"
          margin="normal"
          sx={{
            width: "45%",
          }}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddDriver}
          sx={{ height: 50 }}
        >
          Add Driver
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Driver Name
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Email
              </TableCell>
              {/* <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Assigned Routes
              </TableCell> */}
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {driverPagination.map((driver) => (
              <TableRow key={driver._id}>
                <TableCell>{driver.fullName}</TableCell>
                <TableCell>{driver.email}</TableCell>
                {/* <TableCell>
                  <Tooltip
                    title={
                      driver.deliveryRoutes?.length
                        ? driver.deliveryRoutes
                            .map((route) => route.routeName)
                            .join(", ")
                        : "No Routes Assigned"
                    }
                  >
                    <Typography
                      noWrap
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {driver.deliveryRoutes
                        ?.map((route) => route.routeName)
                        .join(", ") || "No Routes Assigned"}
                    </Typography>
                  </Tooltip>
                </TableCell> */}

                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleEditDriver(driver)}
                    style={{ marginRight: "10px" }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleDeleteOpen(driver._id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredDrivers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Add Driver Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <DialogTitle>Add Driver</DialogTitle>
        <DialogContent>
          <TextField
            label="Full Name"
            fullWidth
            margin="normal"
            value={newDriver.fullName}
            onChange={(e) =>
              setNewDriver({ ...newDriver, fullName: e.target.value })
            }
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={newDriver.email}
            onChange={(e) =>
              setNewDriver({ ...newDriver, email: e.target.value })
            }
          />
          <TextField
            label="password"
            fullWidth
            margin="normal"
            value={newDriver.password}
            onChange={(e) =>
              setNewDriver({ ...newDriver, password: e.target.value })
            }
          />
          {/* 
          {selectedRoutes.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
              {selectedRoutes.map((id) => (
                <Chip
                  key={id}
                  label={routes.find((route) => route._id === id)?.routeName}
                  onDelete={() => handleDeleteChip(id)}
                  color="primary"
                />
              ))}
            </Box>
          )} */}

          {/* Assign Routes */}
          {/* <TextField
            select
            label="Assign Routes"
            fullWidth
            margin="normal"
            value={selectedRoutes}
            onChange={handleRouteSelect}
            SelectProps={{ multiple: true }}
          >
            {routes.map((route) => (
              <MenuItem key={route._id} value={route._id}>
                {route.routeName}
              </MenuItem>
            ))}
          </TextField> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDriver}
            disabled={!newDriver.fullName || !newDriver.email}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Driver Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <DialogTitle>Edit Driver</DialogTitle>
        <DialogContent>
          <TextField
            label="Full Name"
            fullWidth
            margin="normal"
            value={editingDriver?.fullName || ""}
            onChange={(e) =>
              setEditingDriver({ ...editingDriver, fullName: e.target.value })
            }
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={editingDriver?.email || ""}
            onChange={(e) =>
              setEditingDriver({ ...editingDriver, email: e.target.value })
            }
          />
          {/* {selectedRoutes.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
              {selectedRoutes.map((id) => (
                <Chip
                  key={id}
                  label={routes.find((route) => route._id === id)?.routeName}
                  onDelete={() => handleDeleteChip(id)}
                  color="primary"
                />
              ))}
            </Box>
          )}
          <TextField
            select
            label="Assign Routes"
            fullWidth
            margin="normal"
            value={editingDriver?.routeIds || []}
            onChange={(e) =>
              setEditingDriver({
                ...editingDriver,
                routeIds: e.target.value,
              })
            }
            SelectProps={{
              multiple: true,
            }}
          >
            {routes.map((route) => (
              <MenuItem key={route._id} value={route._id}>
                {route.routeName}
              </MenuItem>
            ))}
          </TextField> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEditedDriver}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteModel}
        onClose={() => openDeleteModel(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteModel(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteDriver();
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DriverManagement;
